import { unwrap } from "@sagot/api-client";
import { HOME_STATS_DAYS, WORKFLOW_BOOKING_ACTION_KIND, type HomeStats } from "@sagot/constants";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { buildKeys } from "../../build/shared/keys";
import { workflowKeys } from "../../engage/workflows/use-workflows";
import { dkKeys } from "../shared/keys";

const REFERENCE_STALE_MS = 60_000;
const HOME_TRIGGER = "home_option";
const LIVE_STATE = "live";

export interface NamedChoice {
  id: string;
  name: string;
}

export interface HomeChoices {
  /** Pickable now (live workflows with the Home trigger / booking actions of the agent). */
  choices: NamedChoice[];
  /** Every known name by id, for the summary line of options that point at something no longer pickable. */
  names: ReadonlyMap<string, string>;
  loading: boolean;
  failed: boolean;
}

interface NamedRow {
  id: string;
  name: string;
}

function toChoices<T extends NamedRow>(rows: readonly T[] | undefined, pickable: (row: T) => boolean, state: { loading: boolean; failed: boolean }): HomeChoices {
  const all = rows ?? [];
  return {
    choices: all.filter(pickable).map(({ id, name }) => ({ id, name })),
    names: new Map(all.map(({ id, name }) => [id, name])),
    ...state,
  };
}

/**
 * Live workflows with the "Visitor picks a Home option" trigger. Shares the Workflows list cache and
 * only fetches once the Home tab is open (members without workflows access just see an empty picker).
 */
export function useHomeWorkflows(enabled: boolean): HomeChoices {
  const api = useApi();
  const ws = useWorkspaceId();
  const list = useQuery({ queryKey: workflowKeys.all(ws), queryFn: () => unwrap(api.GET("/v1/workflows", { params: { query: { origin: "user" } } })), retry: false, enabled, staleTime: REFERENCE_STALE_MS });
  return useMemo(
    () => toChoices(list.data, (workflow) => workflow.trigger === HOME_TRIGGER && workflow.state === LIVE_STATE, { loading: enabled && list.isPending, failed: list.isError }),
    [list.data, list.isPending, list.isError, enabled],
  );
}

/** Booking actions of this agent (shares the Actions page cache). */
export function useAgentBookingActions(agentId: string, enabled: boolean): HomeChoices {
  const api = useApi();
  const ws = useWorkspaceId();
  const list = useQuery({
    queryKey: buildKeys.actions(ws, agentId),
    queryFn: () => unwrap(api.GET("/v1/agents/{agent_id}/actions", { params: { path: { agent_id: agentId } } })),
    retry: false,
    enabled,
    staleTime: REFERENCE_STALE_MS,
  });
  return useMemo(
    () => toChoices(list.data, (action) => action.kind === WORKFLOW_BOOKING_ACTION_KIND, { loading: enabled && list.isPending, failed: list.isError }),
    [list.data, list.isPending, list.isError, enabled],
  );
}

/** Impressions, taps per option (every saved option, 0 when never tapped) and conversations from Home. */
export function useHomeStats(agentId: string, enabled: boolean): UseQueryResult<HomeStats> {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: dkKeys.homeStats(ws, agentId, HOME_STATS_DAYS),
    queryFn: () => unwrap(api.GET("/v1/agents/{agent_id}/widget/home/stats", { params: { path: { agent_id: agentId }, query: { days: HOME_STATS_DAYS } } })),
    enabled,
    staleTime: REFERENCE_STALE_MS,
    retry: false,
  });
}
